import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { AudioLines, Mic, MicOff, PhoneOff, RotateCcw, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMicLevel } from "@/pages/interview/DeviceCheck";
import { cn } from "@/lib/utils";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const STATUS = { connecting: ["Connecting…", "text-caution"], live: ["Live", "text-success"], reconnecting: ["Reconnecting…", "text-caution"], dropped: ["Connection lost", "text-risk"] };

function AiTile({ stream, speaking, caption }) {
  const level = useMicLevel(stream);
  const glow = Math.max(level, speaking ? 0.25 : 0);
  return (
    <div className="relative flex aspect-video flex-col items-center justify-center gap-4 overflow-hidden rounded-[6px] bg-[#1B1E1A] text-white ring-1 ring-white/10" data-testid="live-ai-tile">
      <motion.div className="flex h-24 w-24 items-center justify-center rounded-full bg-brand/20 ring-2 ring-brand" animate={{ scale: 1 + glow * 0.35 }} transition={{ duration: 0.12 }}>
        <AudioLines size={40} className="text-brand-highlight" aria-hidden="true" />
      </motion.div>
      <p className="text-sm font-semibold">AI Interviewer</p>
      <p className="text-xs text-white/70" data-testid="live-ai-state">{speaking ? "Speaking…" : "Listening"}</p>
      {caption && <p className="absolute inset-x-4 bottom-3 line-clamp-2 rounded-lg bg-black/40 px-3 py-1.5 text-center text-xs text-white/90" data-testid="live-caption">{caption}</p>}
    </div>
  );
}

function SelfTile({ cam, muted, speaking }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && cam) ref.current.srcObject = cam; }, [cam]);
  return (
    <div className={cn("relative aspect-video overflow-hidden rounded-[6px] bg-[#1B1E1A] ring-1 ring-white/10 transition-shadow", speaking && !muted && "ring-2 ring-brand")} data-testid="live-self-tile">
      {cam ? <video ref={ref} autoPlay muted playsInline className="h-full w-full -scale-x-100 object-cover" aria-label="Your camera" />
        : <div className="flex h-full items-center justify-center text-sm text-white/70">Voice only</div>}
      <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-risk px-2 py-0.5 text-[11px] font-semibold text-white" data-testid="live-rec-badge"><motion.span className="h-1.5 w-1.5 rounded-full bg-white" animate={{ opacity: [1, 0.3, 1] }} transition={{ repeat: Infinity, duration: 1.2 }} /> REC</span>
      <span className="absolute bottom-3 left-3 flex items-center gap-1 rounded-full bg-black/50 px-2 py-0.5 text-xs text-white">{muted ? <MicOff size={12} /> : <Mic size={12} />} You</span>
    </div>
  );
}

export function CallStage({ call, cam, remote, remaining, muted, onMute, onEnd, onRetry, caption }) {
  const [label, color] = STATUS[call.status] || STATUS.connecting;
  const lost = call.status === "dropped";
  return (
    <section className="space-y-4 rounded-[6px] bg-[#1B1E1A] p-4 sm:p-6" data-testid="live-call">
      <div className="flex flex-wrap items-center gap-3 text-sm text-white">
        <span className={cn("flex items-center gap-1.5 font-medium", color)} role="status" data-testid="live-conn-status">{lost ? <WifiOff size={14} /> : <Wifi size={14} />} {label}</span>
        <span className="ml-auto text-xs text-white/60">Target 5 min · max 10 min</span>
        <span className={cn("font-display text-xl font-semibold tabular-nums", remaining <= 60 && "text-risk")} aria-label={`${remaining} seconds left`} data-testid="live-timer">{fmt(Math.max(0, remaining))}</span>
      </div>
      {lost && (
        <div className="flex flex-wrap items-center gap-3 rounded-[6px] bg-risk-tint px-4 py-3 text-sm text-risk" role="alert" data-testid="live-dropped">
          <span className="flex-1">{call.error || "The connection dropped."} Your recording so far is safe. Rejoin to continue where you left off.</span>
          <Button size="sm" onClick={onRetry} data-testid="live-retry-btn"><RotateCcw size={14} /> Rejoin</Button>
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <AiTile stream={remote} speaking={call.aiSpeaking} caption={caption} />
        <SelfTile cam={cam} muted={muted} speaking={call.userSpeaking} />
      </div>
      <div className="flex items-center justify-center gap-4">
        <button type="button" onClick={onMute} className={cn("flex h-12 w-12 items-center justify-center rounded-full transition-colors", muted ? "bg-white text-ink" : "bg-white/10 text-white hover:bg-white/20")} aria-label={muted ? "Unmute" : "Mute"} aria-pressed={muted} data-testid="live-mute-btn">{muted ? <MicOff size={20} /> : <Mic size={20} />}</button>
        <button type="button" onClick={onEnd} className="flex h-12 items-center gap-2 rounded-md bg-risk px-6 font-semibold text-white transition-opacity hover:opacity-90" data-testid="live-end-btn"><PhoneOff size={18} /> End interview</button>
      </div>
      <p className="text-center text-xs text-white/50">Speak naturally. You can interrupt the interviewer, or ask it to repeat or rephrase a question.</p>
    </section>
  );
}
